
import axios from 'axios';
import DOMPurify from 'dompurify';
import { googleClientId, NODE_BACKEND_URL } from './constant';
import { createRoot } from 'react-dom/client';
import { goToLogin } from './navigation';
import { useTheme } from './theme';
import nativeStorage from './nativeStorage';

const getSelectedSport = () => {
  try {
    const savedSport = nativeStorage.getItem('selectedSport');
    const parsedSport = JSON.parse(savedSport);
    const sportCode = parsedSport?.code || 'CBB';

    // Include all supported sports (college + pro + new sports)
    const supportedSports = [
      // Basketball
      'CBB', 'WBB', 'MBB', 'NBA', 'WNBA',
      // Baseball
      'MLB', 'MiLB', 'NPB', 'KBO',
      // Football
      'CFB', 'PFB',
      // Soccer
      'FC',
      // Hockey
      'NHL', 'MiLH',
      // Formula 1
      'F1',
      // Golf
      'PGA',
    ];
    if (!supportedSports.includes(parsedSport?.code)) {
      return 'CBB';
    }
    return sportCode;
  } catch (error) {
    console.warn('Error getting selected sport, defaulting to CBB:', error);
    return 'CBB';
  }
}

const getSelectedTeam = () => {
  try {
    const savedTeam = nativeStorage.getItem('selectedTeam');
    const parsedTeam = JSON.parse(savedTeam);
    const sportTeam = parsedTeam?.teamID || parsedTeam?.team_id

    return `${sportTeam}`;
  } catch (error) {
    return `3433`;
  }
}

const getSelectedTeamDetail = () => {
  try {
    const savedTeam = nativeStorage.getItem('selectedTeam');
    if (!savedTeam) return {};
    const parsedTeam = JSON.parse(savedTeam);
    return parsedTeam || {};
  } catch (error) {
    console.warn('[helpers] Failed to parse selectedTeam:', error.message);
    return {}; // Return empty object, not string, for safe property access
  }
}

const isCBB = () => {
  return getSelectedSport() == 'CBB' ? true : false
}

// Container for notifications
let container = null;
let rootsMap = new Map(); // Store references to roots

// Create or get container
const getContainer = () => {
  if (container) return container;

  container = document.createElement('div');
  container.id = 'notification-container';
  container.style.position = 'fixed';
  container.style.top = '16px';
  container.style.right = '16px';
  container.style.zIndex = '9999';
  container.style.display = 'flex';
  container.style.flexDirection = 'column';
  container.style.gap = '8px';
  container.style.maxWidth = '350px';
  document.body.appendChild(container);

  return container;
};

// Counter for unique IDs
let notificationId = 0;
let activeNotifications = new Map(); // Track active notifications to prevent duplicates

// Check if debug mode is enabled
const isDebugMode = () => {
  return localStorage.getItem('slam_debug_mode') === 'true' ||
         sessionStorage.getItem('slam_debug_mode') === 'true';
};

// Generate a deduplication key from message content
const getDedupeKey = (type, content) => {
  const baseContent = typeof content === 'string' ? content : JSON.stringify(content);
  return `${type}:${baseContent.substring(0, 100)}`;
};

// Create a custom message API (similar to ant's message)
const message = {
  success: (desc = 'Successfully Complete!', duration = 4000, options = {}) => {
    const dedupeKey = getDedupeKey('success', desc);
    if (!options.allowDuplicate && activeNotifications.has(dedupeKey)) {
      return activeNotifications.get(dedupeKey);
    }
    const id = ++notificationId;
    activeNotifications.set(dedupeKey, id);
    createNotification('success', desc, duration, id, options);
    setTimeout(() => activeNotifications.delete(dedupeKey), duration);
    return id;
  },

  error: (desc = 'Oops Something Went Wrong!', duration = 6000, options = {}) => {
    const dedupeKey = getDedupeKey('error', desc);
    if (!options.allowDuplicate && activeNotifications.has(dedupeKey)) {
      return activeNotifications.get(dedupeKey);
    }
    const id = ++notificationId;
    activeNotifications.set(dedupeKey, id);
    createNotification('error', desc, duration, id, options);
    setTimeout(() => activeNotifications.delete(dedupeKey), duration);
    // Also log to console in debug mode
    if (isDebugMode()) {
      console.error('[SLAM Toast Error]', desc, options.details);
    }
    return id;
  },

  info: (desc = 'Information', duration = 4000, options = {}) => {
    const dedupeKey = getDedupeKey('info', desc);
    if (!options.allowDuplicate && activeNotifications.has(dedupeKey)) {
      return activeNotifications.get(dedupeKey);
    }
    const id = ++notificationId;
    activeNotifications.set(dedupeKey, id);
    createNotification('info', desc, duration, id, options);
    setTimeout(() => activeNotifications.delete(dedupeKey), duration);
    return id;
  },

  warning: (desc = 'Warning', duration = 5000, options = {}) => {
    const dedupeKey = getDedupeKey('warning', desc);
    if (!options.allowDuplicate && activeNotifications.has(dedupeKey)) {
      return activeNotifications.get(dedupeKey);
    }
    const id = ++notificationId;
    activeNotifications.set(dedupeKey, id);
    createNotification('warning', desc, duration, id, options);
    setTimeout(() => activeNotifications.delete(dedupeKey), duration);
    return id;
  },

  // New: Loading state that persists until dismissed
  loading: (desc = 'Processing...', options = {}) => {
    const id = ++notificationId;
    createNotification('loading', desc, 0, id, options); // duration 0 = persistent
    return id;
  },

  // New: API error with detailed info
  apiError: (error, context = '', duration = 8000) => {
    const errorMsg = error?.response?.data?.message ||
                     error?.response?.data?.error ||
                     error?.message ||
                     'An unexpected error occurred';
    const statusCode = error?.response?.status;
    const details = isDebugMode() ? {
      status: statusCode,
      url: error?.config?.url,
      method: error?.config?.method?.toUpperCase(),
      data: error?.response?.data
    } : null;

    const displayMsg = statusCode
      ? `${context ? context + ': ' : ''}${errorMsg} (${statusCode})`
      : `${context ? context + ': ' : ''}${errorMsg}`;

    return message.error(displayMsg, duration, { details });
  },

  remove: (id) => {
    const element = document.getElementById(`notification-${id}`);
    if (element) {
      const root = rootsMap.get(id);
      if (root) {
        root.unmount(); // Unmount the root
        rootsMap.delete(id); // Remove from map
      }
      element.remove();
    }
  },

  // Clear all notifications
  clearAll: () => {
    rootsMap.forEach((root, id) => {
      message.remove(id);
    });
    activeNotifications.clear();
  }
};

// Create a notification and mount it to the DOM using React 18's createRoot
const createNotification = (type, content, duration, id, options = {}) => {
  // OLED: always dark mode
  const isDarkMode = true;

  const div = document.createElement('div');
  div.id = `notification-${id}`;
  getContainer().appendChild(div);

  // Create a root for this container using React 18's API
  const root = createRoot(div);
  rootsMap.set(id, root); // Store the root for later cleanup

  // Get styling based on type and theme
  const styles = getStylesByType(type, isDarkMode);

  // Handle dismissal
  const handleDismiss = () => {
    message.remove(id);
  };

  // Create notification element with enhanced styling
  const notification = (
    <div
      style={{
        padding: '12px 40px 12px 44px',
        borderRadius: '8px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4), 0 2px 4px rgba(0, 0, 0, 0.3)',
        backgroundColor: styles.backgroundColor,
        border: `1px solid ${styles.borderColor}`,
        color: styles.textColor,
        position: 'relative',
        animation: 'slideIn 0.3s ease-out',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        minWidth: '280px',
        maxWidth: '400px',
        backdropFilter: 'blur(8px)',
      }}
    >
      {/* Icon */}
      <div style={{
        position: 'absolute',
        left: '14px',
        top: '12px',
        color: styles.iconColor,
        fontSize: '18px',
        fontWeight: 'bold',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '22px',
        height: '22px',
      }}>
        {type === 'loading' ? (
          <span style={{ animation: 'spin 1s linear infinite', display: 'inline-block' }}>⟳</span>
        ) : (
          styles.icon
        )}
      </div>

      {/* Close button */}
      <button
        onClick={handleDismiss}
        style={{
          position: 'absolute',
          right: '8px',
          top: '8px',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          color: styles.textColor,
          opacity: 0.6,
          fontSize: '16px',
          padding: '4px',
          lineHeight: 1,
          borderRadius: '4px',
          transition: 'opacity 0.2s',
        }}
        onMouseEnter={(e) => e.target.style.opacity = 1}
        onMouseLeave={(e) => e.target.style.opacity = 0.6}
      >
        ×
      </button>

      {/* Content */}
      <div style={{
        fontSize: '14px',
        lineHeight: '20px',
        color: styles.textColor,
        fontWeight: 500,
        paddingRight: '16px',
      }}>
        {content}
      </div>

      {/* Debug details (shown in debug mode) */}
      {options.details && isDebugMode() && (
        <div style={{
          marginTop: '8px',
          padding: '8px',
          backgroundColor: 'rgba(0,0,0,0.3)',
          borderRadius: '4px',
          fontSize: '11px',
          fontFamily: 'Monaco, Consolas, monospace',
          color: '#a0a0a0',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-all',
        }}>
          {typeof options.details === 'string'
            ? options.details
            : JSON.stringify(options.details, null, 2)}
        </div>
      )}

      <style>{`
        @keyframes slideIn {
          from { opacity: 0; transform: translateX(100%); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );

  // Render notification using React 18's API
  root.render(notification);

  // Auto-remove after duration (0 means persistent)
  if (duration > 0) {
    setTimeout(() => {
      message.remove(id);
    }, duration);
  }

  return id;
};

// Get styles based on notification type
const getStylesByType = (type, isDarkMode = false) => {
  const lightTheme = {
    success: {
      backgroundColor: '#f6ffed',
      borderColor: '#b7eb8f',
      iconColor: '#52c41a',
      textColor: '#1a1a1a',
      icon: '✓'
    },
    error: {
      backgroundColor: '#fff2f0',
      borderColor: '#ffccc7',
      iconColor: '#ff4d4f',
      textColor: '#1a1a1a',
      icon: '✕'
    },
    warning: {
      backgroundColor: '#fffbe6',
      borderColor: '#ffe58f',
      iconColor: '#faad14',
      textColor: '#1a1a1a',
      icon: '⚠'
    },
    info: {
      backgroundColor: '#f0f9ff',
      borderColor: '#91d5ff',
      iconColor: '#1890ff',
      textColor: '#1a1a1a',
      icon: 'ℹ'
    },
    loading: {
      backgroundColor: '#f5f5f5',
      borderColor: '#d9d9d9',
      iconColor: '#1890ff',
      textColor: '#1a1a1a',
      icon: '⟳'
    }
  };

  const darkTheme = {
    success: {
      backgroundColor: 'rgba(22, 35, 18, 0.95)',
      borderColor: '#274916',
      iconColor: '#73d13d',
      textColor: '#ffffff',
      icon: '✓'
    },
    error: {
      backgroundColor: 'rgba(42, 18, 21, 0.95)',
      borderColor: '#58181c',
      iconColor: '#ff7875',
      textColor: '#ffffff',
      icon: '✕'
    },
    warning: {
      backgroundColor: 'rgba(43, 38, 17, 0.95)',
      borderColor: '#594214',
      iconColor: '#ffc53d',
      textColor: '#ffffff',
      icon: '⚠'
    },
    info: {
      backgroundColor: 'rgba(17, 27, 38, 0.95)',
      borderColor: '#153450',
      iconColor: '#40a9ff',
      textColor: '#ffffff',
      icon: 'ℹ'
    },
    loading: {
      backgroundColor: 'rgba(30, 30, 30, 0.95)',
      borderColor: '#404040',
      iconColor: '#40a9ff',
      textColor: '#ffffff',
      icon: '⟳'
    }
  };

  const theme = darkTheme; // OLED: always dark
  return theme[type] || theme.info;
};

const successMessage = (desc = 'Successfully Complete!') => {
  return message.success(desc);
};

const errorMessage = (desc = 'Oops Something Went Wrong!') => {
  return message.error(desc);
};

// You can add more functions as needed
const infoMessage = (desc = 'Information') => {
  return message.info(desc);
};

const warningMessage = (desc = 'Warning') => {
  return message.warning(desc);
};

async function submitVideoAnalysis(payload) {
  try {
    const res = await axios.post(
      `${NODE_BACKEND_URL}/api/insert-video-analysis-response`,
      payload,
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: getAuthToken(),
        },
      }
    );

    const data = res.data;

    if (data.success) {
      console.log('✅ Video analysis inserted:', data.data);
      return data?.data;
    } else {
      console.error('❌ Insert failed:', data.message);
      // Return null with error info instead of undefined
      return { error: data.message || 'Insert failed', success: false };
    }
  } catch (error) {
    console.error('❌ submitVideoAnalysis API error:', error?.message || error);

    if (!error?.response && error?.code === 'ERR_NETWORK') {
      // Return error object instead of undefined - let caller decide on redirect
      return { error: 'Network error: Backend server unreachable', success: false, code: 'ERR_NETWORK' };
    }

    // Return error details for other errors
    return {
      error: error?.response?.data?.message || error?.message || 'Unknown error',
      success: false,
      status: error?.response?.status
    };
  }
}

async function updateVideoAnalysis(payload) {
  try {
    const res = await axios.post(
      `${NODE_BACKEND_URL}/api/update-video-status`,
      payload,
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: getAuthToken(), // remove this line if auth not required
        },
      }
    );

    const data = res.data;

    if (data.success) {
      console.log('✅ Video analysis updated:', data);
    } else {
      console.error('❌ Insert failed:', data.message);
    }
    return data
  } catch (error) {
    if (!error?.response && error?.code === 'ERR_NETWORK') {
      goToLogin(); // ✅ Redirect to login on network error
    } else {
      console.error('❌ API call error:', error);
    }
  }
}

async function fetchVideoAnalysis(playerId = null, page = 1, limit = 10, folder_id = null, source_type = null) {
  const userId = getUser()?.id;
  const url = `${NODE_BACKEND_URL}/api/video-analysis`;
  const params = {
    ...(playerId ? { player_id: playerId } : userId ? { user_id: userId } : {}),
    ...(folder_id ? { folder_id } : {}),
    page,
    limit,
  };

  // Add source_type parameter if provided
  if (source_type) {
    params.source_type = source_type;
  }

  try {
    const res = await axios.get(url, {
      params,
      headers: {
        Authorization: getAuthToken(),
      },
    });

    const data = res.data;

    if (data?.success) {
      console.log('🎥 Secure Video Analysis Records:', data.data);
      return {
        videos: data.data,
        total: data.count,
        page: data.page,
        limit: data.limit,
      };
    } else {
      console.warn('⚠️ API response error:', data?.message);
    }
  } catch (error) {
    if (!error?.response && error?.code === 'ERR_NETWORK') {
      goToLogin();
    } else {
      console.error('❌ API call error:', error);
    }
  }

  return {
    videos: [],
    total: 0,
  };
}

const fetchVideoCsv = async (page, limit) => {
  const userId = getUser()?.id;
  const url = `${NODE_BACKEND_URL}/api/get-csv-video-data`;

  const params = { user_id: userId, page, limit }

  try {
    const res = await axios.get(url, {
      params,
      headers: {
        Authorization: getAuthToken(),
      },
    });

    const data = res?.data;

    if (data?.success) {
      console.log('🎥 Secure Video Records:', data.data);
      return {
        data: data.data,
        count: data?.count
      };
    } else {
      console.warn('⚠️ API response error:', data?.message);
    }
  } catch (error) {
    if (!error?.response && error?.code === 'ERR_NETWORK') {
      goToLogin(); // ✅ Redirect to login on network error
    } else {
      console.error('❌ API call error:', error);
    }
  }

  return [];
}

function getPlayerIdFromUrl() {
  const parts = window?.location?.pathname?.split('/');
  const id = parts?.[parts?.length - 1];
  // Check if ID exists and is a numeric string
  if (id && /^\d+$/.test(id)) {
    return id;
  }

  // ID is missing or invalid
  return null;
}

const getUser = () => {
  let user = nativeStorage.getItem('user');
  try {
    return JSON.parse(user) || {}
  } catch (error) {
    return {}
  }
}

const getSetting = () => {
  let setting = nativeStorage.getItem('setting');
  try {
    return JSON.parse(setting) || {}
  } catch (error) {
    return {}
  }
}

const getAllSports = () => {
  let sports = nativeStorage.getItem('sports');
  try {
    return JSON.parse(sports) || []
  } catch (error) {
    return []
  }
}

const processTextFormatting = (text) => {
  if (!text) return '';

  let processed = text;

  // Headings (###, ##, #)
  processed = processed.replace(/^### (.*)$/gim, '<h3 class="text-lg font-semibold mt-4 mb-2">$1</h3>');
  processed = processed.replace(/^## (.*)$/gim, '<h2 class="text-xl font-bold mt-4 mb-2">$1</h2>');
  processed = processed.replace(/^# (.*)$/gim, '<h1 class="text-2xl font-bold mt-6 mb-3">$1</h1>');

  // Bold (**text** or __text__)
  processed = processed.replace(/(\*\*|__)(.*?)\1/g, '<strong class="font-bold">$2</strong>');

  // Italic (*text* or _text_) — avoid bold conflict
  processed = processed.replace(/(?<!\*)\*(?!\*)(.*?)\*(?!\*)/g, '<em class="italic">$1</em>');
  processed = processed.replace(/(?<!_)_(?!_)(.*?)_(?!_)/g, '<em class="italic">$1</em>');

  // Inline code (`code`)
  processed = processed.replace(/`([^`]+)`/g, '<code class="bg-gray-100 px-1 rounded font-mono text-sm">$1</code>');

  // Links [text](url)
  processed = processed.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-yellow-500 hover:underline">$1</a>');

  // Strikethrough (~~text~~)
  processed = processed.replace(/~~(.*?)~~/g, '<span class="line-through">$1</span>');

  // Highlighting (==text==)
  processed = processed.replace(/==(.*?)==/g, '<mark class="bg-yellow-100 px-1 rounded">$1</mark>');

  // Subscript (~text~) — single tilde only
  processed = processed.replace(/(?<!~)~(.*?)~(?!~)/g, '<sub>$1</sub>');

  // Superscript (^text^)
  processed = processed.replace(/\^([^^]+)\^/g, '<sup>$1</sup>');

  // Line breaks → <br />
  processed = processed.replace(/\n/g, '<br />');

  // Cleanup: remove any unmatched markdown symbols that may have leaked
  processed = processed.replace(/(?<!\w)[*_~^=`#]+(?!\w)/g, '');

  // Return as React element with sanitized HTML
  return <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(processed) }} />;
};


export const getTeamsBySport = async (userId, sportCode, id) => {
  try {
    // Deep unwrap nested arrays to get the actual string value
    // This handles the bug where sport_type could be [[[[["EURO"]]]]]]
    const unwrapNestedArray = (val) => {
      while (Array.isArray(val) && val.length > 0) {
        val = val[0];
      }
      return val;
    };
    const normalizedSportCode = unwrapNestedArray(sportCode);
    const normalizedId = unwrapNestedArray(id);

    if (!normalizedSportCode) {
      throw new Error('Sport is required');
    }

    console.log('normalizedSportCode:', normalizedSportCode);

    // Check if the sport is MBB - Men's Basketball
    if (normalizedSportCode === "MBB") {
      try {
        // Use the special MBB endpoint for team stats
        const mbbResponse = await axios.get(
          "https://slam-all-python-359065791766.us-central1.run.app/MBB/team-stats-mia/?schema=MBB",
          {
            headers: {
              Authorization: getAuthToken(),
            },
          }
        );

        console.log('MBB Teams API raw response:', mbbResponse);

        if (mbbResponse?.data?.data && Array.isArray(mbbResponse?.data?.data)) {
          // Process the MBB teams response and filter for the matching team
          const teamsData = mbbResponse.data.data
            .filter(team => team?.rank == normalizedId)
            .map(team => ({
              team_id: String(team?.rank),
              team_name: team?.team || 'Unknown Team',
              full_name: team?.team || '',
              name: team?.team || '',
              nickname: team?.team || '',
              abbreviation: team?.team || '',
              logo_url: team?.logo_url || team?.logo || '',
              conference: team?.Conference || team?.conference || '',
              teamID: team?.rank
            }));

          if (teamsData.length > 0) {
            return { success: true, teams: teamsData[0] };
          }
        }
        // If MBB endpoint failed or team not found, continue to use the regular endpoint
      } catch (mbbError) {
        console.error('MBB Teams API error:', mbbError);
        // Continue to use the regular endpoint as fallback
      }
    }

    // For non-MBB sports or if MBB specific endpoint fails, use the default endpoint
    const url = `${NODE_BACKEND_URL}/api/user/${userId}/sports/${normalizedSportCode}/teams`;
    const response = await axios.get(url, {
      headers: {
        Authorization: getAuthToken()
      }
    });

    console.log('Teams API raw response:', response);

    const resData = response.data?.data || response.data;
    if (!response.data?.success || !Array.isArray(resData.teams)) {
      throw new Error('Invalid response format from API.');
    }

    // Filter for the matching team and then map to the desired structure
    const teamsData = resData.teams
      .filter(team => team?.TeamID == normalizedId)
      .map(team => ({
        team_id: String(team?.TeamID),
        team_name: team?.TeamName || team?.Name || 'Unknown Team',
        full_name: team?.FullName || team?.TeamName || '',
        name: team?.Name || team?.TeamName || '',
        nickname: team?.Nickname || '',
        abbreviation: team?.Abbrev || '',
        logo_url: team?.logo_url || team?.logo || '',
        conference: team?.Conference || team?.conference || '',
        teamID: team?.TeamID
      }));

    // Sort alphabetically (if more than one team exists)
    teamsData.sort((a, b) => a.team_name.localeCompare(b.team_name));

    return { success: true, teams: teamsData?.[0] };
  } catch (error) {
    if (!error?.response && error?.code === 'ERR_NETWORK') {
      goToLogin(); // ✅ Redirect to login on network error
    }
    console.error('getTeamsBySport error:', error);
    return { success: false, teams: [], error: error.message };
  }
};

export const getUserSports = async (userId, code) => {
  try {
    // Deep unwrap nested arrays to get the actual string value
    const unwrapNestedArray = (val) => {
      while (Array.isArray(val) && val.length > 0) {
        val = val[0];
      }
      return val;
    };
    const normalizedCode = unwrapNestedArray(code);

    const url = `${NODE_BACKEND_URL}/api/user/${userId}/sports`;
    const response = await axios.get(url, {
      headers: {
        Authorization: getAuthToken()
      }
    });

    const resData = response.data?.data || response.data;
    if (!response.data?.success || !Array.isArray(resData.sports)) {
      throw new Error('Invalid response format from API.');
    }

    return {
      success: true,
      sports: resData.sports?.find((v) => v?.code == normalizedCode)
    };
  } catch (error) {
    if (!error?.response && error?.code === 'ERR_NETWORK') {
      goToLogin(); // ✅ Redirect to login on network error
    }
    console.error('getUserSports error:', error);
    return {
      success: false,
      sports: {},
      error: error.message
    };
  }
};

export const getAllSportsByUserId = async (userId) => {
  try {
    const url = `${NODE_BACKEND_URL}/api/user/${userId}/sports`;
    const response = await axios.get(url, {
      headers: {
        Authorization: getAuthToken()
      }
    });

    const allResData = response.data?.data || response.data;
    if (!response.data?.success || !Array.isArray(allResData.sports)) {
      throw new Error('Invalid response format from API.');
    }

    const sports = allResData.sports
    nativeStorage.setItem('sports', JSON.stringify(sports))
  } catch (error) {
    if (!error?.response && error?.code === 'ERR_NETWORK') {
      goToLogin(); // ✅ Redirect to login on network error
    }
    console.error('getUserSports error:', error);
    return {
      success: false,
      sports: {},
      error: error.message
    };
  }
};

const getUserSettings = async (userId = '') => {
  try {
    if (!userId) {
      return true
    }
    const url = `${NODE_BACKEND_URL}/api/setting?userId=${userId}`;
    const response = await axios.get(url, {
      headers: {
        Authorization: getAuthToken()
      }
    });
    nativeStorage.setItem('setting', JSON.stringify((response?.data?.setting)))
  } catch (error) {
    console.log('getUserSettings error:', error);
    return { success: false, sports: {}, error: error.message };
  }
};

const getAuthToken = () => {
  try {
    const token = getUser()?.token
    return token ? `Bearer ${token}` : null;
  } catch (error) {
    console.log('ERROR IN GET AUTH TOKEN', error)
    return `Bearer `
  }
}

function formatFileSize(fileSizeInBytes) {
  // Check if input is a valid number
  if (typeof fileSizeInBytes !== 'number' || isNaN(fileSizeInBytes)) {
    return '0B'
  }

  // Define conversion thresholds and units
  const units = [
    { name: 'B', threshold: 1, divisor: 1 },
    { name: 'KB', threshold: 1024, divisor: 1024 },
    { name: 'MB', threshold: 1024 * 1024, divisor: 1024 * 1024 },
    { name: 'GB', threshold: 1024 * 1024 * 1024, divisor: 1024 * 1024 * 1024 }
  ];

  // Find the most appropriate unit
  const unit = units.findLast(u => fileSizeInBytes >= u.threshold) || units[0];

  // Convert and round the size
  const convertedSize = Number((fileSizeInBytes / unit.divisor).toFixed(2));

  // Return formatted string without space
  return `${convertedSize}${unit.name}`;
}

const refreshToken = async () => {
  const user = getUser()
  if (!user || Object.keys(user).length === 0) return;

  const token = user?.token;
  if (!token?.length) {
    return goToLogin(true)
  }
  try {
    const res = await axios.get(`${NODE_BACKEND_URL}/api/refresh-token?token=${token}`, {
      headers: {
        Authorization: getAuthToken()
      }
    });

    if (res?.data?.success && (res.data?.data?.token || res.data?.token)) {
      user.token = res.data.data?.token || res.data.token;
      nativeStorage.setItem('user', JSON.stringify(user));
      console.log('Token refreshed and updated in sessionStorage');
    }
    else {
      errorMessage('Session expired. Please login again.')
      goToLogin(true)
    }
  } catch (err) {
    console.log('REFRESH TOKEN RESPONSE', err)
    if (err?.response?.status === 401) {
      goToLogin(true)
      errorMessage('Session expired. Please login again.')
      // Optional: handle logout here
    } else {
      console.error('Failed to refresh token:', err);
    }
  }
}

const sortStatsByYear = (statsArray) => {
  // Create a copy of the array to avoid mutating the original
  const statsCopy = [...statsArray];

  // Find and remove the "Career" entry if it exists
  const careerIndex = statsCopy.findIndex(stat => stat.year === 'Career');
  let careerEntry = null;

  if (careerIndex !== -1) {
    careerEntry = statsCopy.splice(careerIndex, 1)[0];
  }

  // Sort the remaining entries by year in descending order
  const sortedStats = statsCopy.sort((a, b) => {
    // Convert year to number for numerical comparison
    const yearA = parseInt(a.year, 10);
    const yearB = parseInt(b.year, 10);

    // Sort in descending order (newest first)
    return yearB - yearA;
  });

  // Append the Career entry at the end if it existed
  if (careerEntry) {
    sortedStats.push(careerEntry);
  }

  return sortedStats;
}

function capitalizeFirstWord(sentence) {
  // const sentence = word?.length > 20 ? `${word?.slice(0, 20)}...` : word
  const words = sentence?.split(' ');

  if (words?.length > 0) {
    words[0] = words[0]?.charAt(0)?.toUpperCase() + words[0]?.slice(1)?.toLowerCase();
  }
  return words?.join(' ');
}

const getSportTitle = () => {
  const code = getSelectedSport();

  switch (code) {
    case 'CBB':
      return "College Men's Baseball Roster";
    case 'WBB':
      return "Women's Basketball Roster";
    case 'MBB':
      return "Men's Basketball Roster";
    default:
      return "College Men's Baseball Roster"; // default fallback
  }
};

/**
 * Check if a video file is an iOS-native format that may need server-side transcoding.
 * iOS records in HEVC/H.265 inside .MOV containers, which can cause Gemini analysis failures.
 *
 * @param {File} file - The video File object
 * @returns {{ isIOS: boolean, reason: string }}
 */
const detectIOSVideo = (file) => {
  if (!file) return { isIOS: false, reason: '' };

  const name = (file.name || '').toLowerCase();
  const type = (file.type || '').toLowerCase();

  // .MOV container (QuickTime)
  if (name.endsWith('.mov') || type === 'video/quicktime' || type === 'video/x-quicktime') {
    return { isIOS: true, reason: 'QuickTime .MOV container detected' };
  }

  // HEVC/H.265 indicators
  if (name.endsWith('.hevc') || name.endsWith('.heif') || type === 'video/hevc') {
    return { isIOS: true, reason: 'HEVC/H.265 codec detected' };
  }

  // iOS Safari sometimes sends video/mp4 for .mov files recorded on-device
  // Check for very large file sizes typical of iOS 4K recording (~300-400MB/min)
  if (type === 'video/mp4' && file.size > 100 * 1024 * 1024) {
    // Large MP4 — may be iOS. Not definitive, but worth flagging
    return { isIOS: false, reason: 'large_mp4' };
  }

  return { isIOS: false, reason: '' };
};

const uploadFileToGCS = async (file, metadata = {}, gcs_folder_name = 'videos', progressCallback = null) => {
  // Step 1: Get signed upload URL from backend directly (bypasses gateway CORS issues)
  let uploadUrlResponse;
  try {
    uploadUrlResponse = await axios.post(`${NODE_BACKEND_URL}/api/generate-upload-url`, {
      filename: file.name,
      metadata,
      gcs_folder_name,
      contentType: file.type
    }, {
      headers: {
        Authorization: getAuthToken()
      }
    });
  } catch (error) {
    // Extract detailed error info from response
    const errorDetails = error.response?.data;
    const errorMsg = errorDetails?.error || errorDetails?.details || error.message || 'Failed to generate upload URL';
    const gcsError = errorDetails?.gcsError;

    console.error('[uploadFileToGCS] Failed to get upload URL:', {
      message: errorMsg,
      status: error.response?.status,
      gcsStatus: errorDetails?.gcsStatus,
      gcsError
    });

    // Use new API error toast if available
    message.apiError(error, 'Video upload');
    throw new Error(`Upload URL generation failed: ${errorMsg}`);
  }

  // The backend respond() helper wraps payload in { success, data: {...} } envelope
  const responsePayload = uploadUrlResponse?.data?.data || uploadUrlResponse?.data || {};
  const { gsUrl, headers, sessionUrl, success } = responsePayload;

  if (!success || !sessionUrl) {
    const errorMsg = uploadUrlResponse?.data?.error || 'Invalid upload URL response';
    message.error(`Upload failed: ${errorMsg}`);
    throw new Error(errorMsg);
  }

  // Step 2: Upload the file directly to GCS (or local) with progress tracking
  const isLocalUpload = sessionUrl.includes('/api/local-upload/');
  try {
    const uploadRes = await axios.put(sessionUrl, file, {
      headers: {
        'Content-Type': file.type,
        ...headers,
        ...(isLocalUpload ? { Authorization: getAuthToken() } : {}),
      },
      onUploadProgress: (progressEvent) => {
        if (progressCallback && typeof progressCallback === 'function') {
          // Calculate percentage
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          progressCallback(percentCompleted);
        }
      }
    });

    if (uploadRes?.status !== 200) {
      throw new Error('Upload to GCS failed with status: ' + uploadRes?.status);
    }
  } catch (error) {
    console.error('[uploadFileToGCS] Failed to upload file:', error);
    message.apiError(error, 'File upload to storage');
    throw error;
  }

  // Call progress callback one last time to ensure 100%
  if (progressCallback && typeof progressCallback === 'function') {
    progressCallback(100);
  }

  return gsUrl; // Return the GCS URL for future reference
}

function createDummyMP4FileObject() {
  // Valid base64-encoded MP4 binary (1 second black video)
  const base64MP4 =
    "AAAAIGZ0eXBtcDQyAAAAAG1wNDFtcDQyaXNvbWF2YzEAAAAAGG1vb3YAAAAMbTR2aGQAAAAAAAABAAEAAABHbWRpYQAAAAx0bWRoAAAAAAABAAEAAAAZGWR0cmFrAAAAXHRraGQAAAADAAAAAAAAAAEAAAAAAE1kaWEAAAAgbWRoZAAAAAABAAEAAAAZGWRpbmYAAAAcZGluZgAAABxkcmVmAAAADG1wNDFtcDQyaXNvbWF2YzEAAAAAAAAAAAAAAAAAAAAAbXN0YmwAAABobXN0c2QAAAAAAAAAAQAAAABDb2RjAAAAAAAAAAEAAAABAAABAAAAAQAAAG1kYXQAAAAAAAAB";

  // Decode base64 to binary
  const binary = atob(base64MP4);
  const byteArray = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    byteArray[i] = binary.charCodeAt(i);
  }

  // Convert to a File object
  const file = new File([byteArray], "youtubeVideo.mp4", {
    type: "video/mp4",
    lastModified: new Date().getTime(),
  });

  return file;
}

const cleanName = (encodedName) => {
  if (!encodedName) return '';
  return decodeURIComponent(encodedName).replace(/^\/+/, '');
}

// Backend-mediated upload for non-video/non-image files (CSV, JSON, PDF, code, etc.).
// Frontend -> /api/chat/upload-file -> multer -> GCS. Returns the gs:// URI, or
// null (with base64 content as a fallback) if GCS isn't configured/reachable.
const uploadFileViaBackend = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  const user = getUser();
  if (user?.id) formData.append('userId', user.id);

  const res = await axios.post(
    `${NODE_BACKEND_URL}/api/chat/upload-file`,
    formData,
    {
      headers: {
        Authorization: getAuthToken(),
        ...(user?.id ? { 'x-user-id': user.id } : {}),
      },
    }
  );

  const payload = res?.data?.data || res?.data || {};
  const fileObj = payload.file || {};
  return {
    gsUri: fileObj.gcsPath || null,
    mimeType: fileObj.mimeType || file.type,
    displayName: fileObj.displayName || file.name,
    inlineBase64: fileObj.content || null,
  };
};

export const uploadTrainingVideos = async (files, progressCallback, folderId = null, source_type = 'training_statistics') => {
  try {
    console.log('Multiple file:', files.length);

    // Process each video file in parallel
    const analysisPromises = files.map(async (videoFile, index) => {
      const controller = new AbortController();
      const timeoutDuration = 300000; // 5 minutes per video
      const timeoutId = setTimeout(() => {
        console.log(`Request timed out for ${videoFile.name} after ${timeoutDuration / 1000} seconds`);
        controller.abort();
      }, timeoutDuration);
      try {
        const user = getUser()

        const submitPayload = {
          response: '',
          user_id: user?.id,
          video_name: videoFile?.name,
          folderId: folderId,
          source_type: source_type,
          size: 0,
          sport: getSelectedSport(),
        }
        const analysis = await submitVideoAnalysis(submitPayload)

        // Prepare form data for this video
        const metadata = {
          team: getSelectedTeamDetail()?.name,
          schema: getSelectedSport(),
          ingest_data: true,
        }

        // Call the modified uploadFileToGCS with progress tracking
        const getGSURL = await uploadFileToGCS(
          videoFile.file || videoFile, // Handle both objects with file property and direct file objects
          metadata,
          'URI-resource',
          (progress) => {
            // Call the progressCallback with the file ID and progress
            if (progressCallback && typeof progressCallback === 'function') {
              progressCallback(videoFile.id, progress);
            }
          }
        );
        await updateVideoAnalysis({
          id: analysis?.id,
          gs_uri: getGSURL,
        })
        console.log('uploadFileToGCS', getGSURL);
        return { id: videoFile.id, success: true, url: getGSURL };
      } catch (error) {
        clearTimeout(timeoutId);
        console.error(`Error processing files ${videoFile.name}:`, error);
        // Report error through callback
        if (progressCallback && typeof progressCallback === 'function') {
          progressCallback(videoFile.id, -1, error.message); // -1 indicates error
        }
        return { id: videoFile.id, success: false, error: error.message };
      }
    });

    // Wait for all analysis requests to complete
    const results = await Promise.allSettled(analysisPromises);

    // Process results
    return results.map((result) => {
      if (result.status === 'fulfilled') {
        return result.value;
      } else {
        // Handle rejection
        console.error('Promise rejected:', result.reason);
        return { success: false, error: result.reason };
      }
    });
  } catch (error) {
    console.error('Error in multiple video analysis:', error);
    throw error;
  }
};

const isNorthCarolinaTeam = () => {
  const team = getSelectedTeamDetail();
  return (team?.name === 'North Carolina' || team?.name === 'UNC' ||
    team?.team_name === 'North Carolina' || team?.team_name === 'UNC') ? true : false;
}

// Add this function to dispatch a custom event when team changes
const notifyTeamChange = () => {
  // Create and dispatch a custom event for team change
  const teamChangeEvent = new Event('teamChange');
  window.dispatchEvent(teamChangeEvent);
}

// Enable debug mode for toasts: localStorage.setItem('slam_debug_mode', 'true')
const enableDebugToasts = () => {
  localStorage.setItem('slam_debug_mode', 'true');
  console.log('🔧 Debug toast mode enabled - API errors will show detailed info');
};

const disableDebugToasts = () => {
  localStorage.removeItem('slam_debug_mode');
  console.log('Debug toast mode disabled');
};

const clearAuth = () => {
  nativeStorage.removeItem('user');
  nativeStorage.removeItem('selectedSport');
  nativeStorage.removeItem('selectedTeam');
  nativeStorage.removeItem('sports');
  nativeStorage.removeItem('setting');
  localStorage.removeItem('slam_user');
  localStorage.removeItem('slam_selectedSport');
  localStorage.removeItem('slam_selectedTeam');
};

const isValidReturnUrl = (url) => {
  if (!url || typeof url !== 'string') return false;
  const trimmed = url.trim();
  if (/^[a-zA-Z][a-zA-Z0-9+.-]*:/i.test(trimmed)) return false;
  if (trimmed.startsWith('//') || trimmed.includes('\\')) return false;
  return trimmed.startsWith('/');
};

export {
  successMessage,
  errorMessage,
  infoMessage,
  warningMessage,
  message, // Export the full message object for advanced usage
  enableDebugToasts,
  disableDebugToasts,
  isCBB,
  getSelectedSport,
  submitVideoAnalysis,
  getSelectedTeam,
  getPlayerIdFromUrl,
  fetchVideoAnalysis,
  getUser,
  processTextFormatting,
  getAuthToken,
  formatFileSize,
  refreshToken,
  sortStatsByYear,
  capitalizeFirstWord,
  getSportTitle,
  fetchVideoCsv,
  uploadFileToGCS,
  uploadFileViaBackend,
  updateVideoAnalysis,
  createDummyMP4FileObject,
  getSetting,
  getUserSettings,
  getAllSports,
  cleanName,
  getSelectedTeamDetail,
  isNorthCarolinaTeam,
  notifyTeamChange,
  clearAuth,
  isValidReturnUrl,
  detectIOSVideo,
}